import { KeysOfType } from './type-helpers';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- This is OK to disable be cause we cannot know all the possible property types here and we just have to hope it will always work
type IIndexableUnsafeObject = Record<string, any>;

/**
 * @description A simple test to see if the passed in value appears to be a JavaScript Array or not.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any  --  This is OK for now to make things work, but we need to fix this to allow for strong typing
export function arrayIsTruthy<T = any>(arr: Array<T>): arr is Array<T> {
  //check if the value is an array, and that the array has at least one value in it.
  return Array.isArray(arr) && arr.length > 0;
}

/**
 * @description When two arrays are passed in, this determines if the values are all the same or not
 * NOTE: This does NOT do a deep equality check on objects!
 */
export function isSameArray<T>(array: Array<T>, otherArray: Array<T>): boolean {
  // compare lengths - can save a lot of time
  if (otherArray.length !== array.length) {
    return false;
  }

  for (let i = 0, l = otherArray.length; i < l; i++) {
    // Check if we have nested arrays
    if (otherArray[i] instanceof Array && array[i] instanceof Array) {
      // recurse into the nested arrays
      if (otherArray[i] !== array[i]) {
        return false;
      }
    } else if (otherArray[i] !== array[i]) {
      // Warning - two different object instances will never be equal: {x:20} != {x:20}
      return false;
    }
  }
  return true;
}

/**
 * @description Allows you to pass an object, and the property path as a dot-separated string
 * @returns the property value if it exists, and `undefined` if it does not. You can also provide a default value to be returned instead
 * @todo Change the return type to `T | R | undefined` and fix all usages so we get strong typing where this is used!
 */
// eslint-disable-next-line complexity -- No great way to simplify or split this logic up into small pieces
export function getProperty<T extends IIndexableUnsafeObject, R = unknown>(
  obj: T | undefined,
  path?: string,
  defaultValue?: R,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any  --  This is OK for now to make things work, but we need to fix this to allow for strong typing
): any {
  const isObjDefined = Boolean(obj);
  const isPathDefined = typeof path === 'string' && path !== '';
  if (defaultValue != null && (!isObjDefined || !isPathDefined)) {
    //either no object or no path provided, so we can't do anything but return the default value
    return defaultValue;
  } else if (!isObjDefined) {
    //no object provided, so there's nothing we can do here
    return undefined;
  } else if (!isPathDefined) {
    //no path provided, so just return the object that was put in
    return obj;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return -- This is OK to disable be cause we cannot know all the possible property types here and we just have to hope it will always work
  const value: IIndexableUnsafeObject | undefined = path.split('.').reduce((o, p) => o?.[p], obj);
  if (value == null && defaultValue != null) return defaultValue; //optional value to return if the property doesn't exist.
  return value;
}

/**
 * @description Returns a copy of the passed in array, but all duplicate values are removed
 */
export function removeDuplicates<T>(arr: Array<T>): Array<T> {
  //this will work for flat arrays
  return [...new Set(arr)];
}

/**
 * @description Returns a copy of the passed in object array, but all duplicate values for the passed in key are removed
 */
export function removeDuplicatesByProperty<T>(arr: Array<T>, key: keyof T): Array<T> {
  //use this one for object arrays
  return [...new Map(arr.map((item) => [item[key], item])).values()];
}

/**
 * @description returns an array of the specified property
 * @deprecated ## ❌ Do not use this, it's not type safe!
 * Use `findUniqueV2` instead. This will be deleted once ECC apps ago away*/
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- This is OK to disable be cause we cannot know all the possible property types here and we just have to hope it will always work
export function findUnique<R, T = any>(array: Array<T>, path: keyof T | string, shouldReturnFalsy = false): Array<R> {
  const unique: Array<R> = [];
  array.forEach((element) => {
    const indexedProperty = getProperty(element as object, path as string) as R | undefined;
    if (indexedProperty === undefined) return;
    const found = unique.indexOf(indexedProperty);
    if (found === -1) unique.push(indexedProperty);
  });
  if (shouldReturnFalsy) {
    return unique;
  } else {
    return unique.filter((x) => Boolean(x));
  }
}

/**
 * @describe Return an array of strings of all the unique values in the array for that property
 * @example findUniqueV2([{foo: 'a'}, {foo: 'a'}, {foo: 'b'}, {foo: 'c'}], 'foo'); // ['a', 'b', 'c']
 * @param keyName The string property on each object in the passed array you want to find the unique values of
 * @param shouldReturnFalsyValues indicates that you want values like "" null or undefined in the returned array
 */
export function findUniqueV2<T extends object, K extends KeysOfType<T, string>>(
  array: Array<T>,
  keyName: K,
  returnFalsy = false,
): Array<T[K]> {
  const seen = new Set<T[K]>();

  for (const item of array) {
    const value = item[keyName] as T[K];
    if (value !== undefined && (returnFalsy || Boolean(value))) {
      seen.add(value);
    }
  }

  return Array.from(seen);
}

/**
 * @description Overwrite each object property of the `receiver` (2nd parameter) with the values of the `sender` (1st parameter).
 * @param sender The object which the `receiver` will get the property values from. This object WILL NOT not be modified
 * @param receiver The object that will have it's values overwritten from the `sender`. This object WILL be modified!
 * @param shouldFail Optional and defaults to `false`. Set to `true` to cause an error to be thrown if the property is not found.
 */
export function syncObjectProperties(sender: IIndexableUnsafeObject, receiver: IIndexableUnsafeObject, shouldFail = false): void {
  Object.keys(sender).forEach((key) => {
    try {
      if (shouldFail && receiver[key] == null) return;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- This is OK to disable be cause we cannot know all the possible property types here and we just have to hope it will always work
      receiver[key] = sender[key];
    } catch (e) {
      if (shouldFail) throw e;
    }
  });
}

/**
 * @description Returns a copy of a class as a plain object, which will omit the constructor and any methods.
 * Mostly this is a way to help us type things correctly for getting around this lint rule: https://typescript-eslint.io/rules/no-misused-spread/
 */
export function classToPlainObject(classToConvert: undefined | null): object;
export function classToPlainObject<T>(classToConvert: T): { [K in keyof T]: T[K] };
export function classToPlainObject<T extends object | undefined | null>(classToConvert: T): { [K in keyof T]: T[K] } | object {
  if (classToConvert == null) return {};
  return { ...classToConvert };
}

/**
 * @description Returns a copy of the object, but with the passed properties omitted. This does not work with nested properties.
 * @example omitProperties({a:1, b:2, c:3, d:4}, ['b', 'd']); //=> {a:1, c:3}*/
export function omitProperties<T extends NonNullable<object>, K extends keyof T>(obj: T, propsToOmit: Array<K>): Omit<T, K> {
  const omitted = {} as Omit<T, K>;
  Object.keys(obj).forEach((key) => {
    if (!propsToOmit.includes(key as K)) {
      //@ts-expect-error -- We are kind of forcing the types here, it works but I'm not sure how to make TS happy with this
      omitted[key] = obj[key as keyof T];
    }
  });
  return omitted;
}
